import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { clinicalTrialToCitation, filterClinicalTrials } from '../api/clinicalTrialCitation';
import { filterPublications, publicationToCitation } from '../api/publicationCitation';
import { type Citation, describeCitation, insertCitation } from '../model/citation';
import {
  type ActionDotPhrase,
  addMissingDot,
  completeDotPhrase,
  DOT_PHRASES,
  type DotPhrase,
  type DotPhraseTrigger,
  parseTrailingDotPhrase,
  removeActionToken,
} from '../model/dotPhrases';
import type { DotPhraseMenuItem, DotPhraseMenuProps, DotPhraseMenuSection } from '../model/menu';

import { HALF_SECOND } from '@/constants/time';
import { getAllUserClinicalTrialsQueryOptions } from '@/services/getUserClinicalTrials';
import { getAllUserPublicationsQueryOptions } from '@/services/getUserPublications';

const MAX_PROFILE_ROWS = 5;

type ProfilePhraseCopy = {
  heading: string;
  findTitle: (query: string) => string;
  loading: string;
  failed: string;
  noneOnProfile: string;
  noMatches: string;
};

/** Phrases that list and filter the expert's own profile records. */
const PROFILE_PHRASES: Partial<Record<DotPhraseTrigger, ProfilePhraseCopy>> = {
  mypubs: {
    heading: 'Your publications',
    findTitle: (query) => `Find “${query}” in your publications`,
    loading: 'Loading your publications…',
    failed: 'Could not load your publications.',
    noneOnProfile: 'No publications on your profile yet.',
    noMatches: 'No publications match. Try a title word, journal or year.',
  },
  mytrials: {
    heading: 'Your clinical trials',
    findTitle: (query) => `Find “${query}” in your clinical trials`,
    loading: 'Loading your clinical trials…',
    failed: 'Could not load your clinical trials.',
    noneOnProfile: 'No clinical trials on your profile yet.',
    noMatches: 'No trials match. Try a drug, condition, phase or NCT number.',
  },
};

const OTHER_SHORTCUTS_HEADING = 'Other shortcuts';

type ComposerKeyEvent = {
  key?: string;
  nativeEvent?: { key?: string };
  preventDefault?: () => void;
};

/** A phrase the menu can list: one from the registry, or an action another feature supplies. */
type MenuPhrase = DotPhrase | ActionDotPhrase;

function isAction(phrase: MenuPhrase): phrase is ActionDotPhrase {
  return 'onRun' in phrase;
}

const NO_ACTIONS: ActionDotPhrase[] = [];

type UseDotPhraseMenuParams = {
  /** Turns on the registry's citation phrases, for experts only. Action phrases are offered whenever they are passed. */
  enabled: boolean;
  actionPhrases?: ActionDotPhrase[];
  userId: string;
  isExpert: boolean;
  draftMessage: string;
  setDraftMessage: (text: string) => void;
  /** Returns focus to the composer after a pick; a mouse press on a row moves it away on web. */
  focusComposer?: () => void;
};

type MenuView = {
  heading: string;
  items: DotPhraseMenuItem[];
  status?: string;
  isLoading?: boolean;
  secondary?: DotPhraseMenuSection;
};

const NO_ITEMS: DotPhraseMenuItem[] = [];

/**
 * Drives composer dot phrases, Epic SmartPhrase style: `.` plus letters lists
 * matching phrases, and a completed phrase opens that phrase's picker. Picking
 * a result expands the token into an inline `[n]` citation. Nothing is sent —
 * the expert reviews the expanded draft and sends it themselves.
 *
 * - `.mypubs [filter]` and `.mytrials [filter]` list the expert's own profile
 *   publications and clinical trials, fetched once and filtered locally as
 *   they type. Experts only.
 * - While a phrase is being typed, the phrases it does not match are listed
 *   under "Other shortcuts", so a reader who knows one learns the rest. A
 *   dot-word that matches nothing lists them all, passively, so a typo shows
 *   what exists without taking over Enter.
 *
 * Returns the menu's props, or null while it is closed, and a key handler for web.
 * It draws nothing: the chat renders `DotPhraseMenu` with those props.
 * While the menu is open the handler owns Enter and Tab even when there is no
 * row yet, so an Enter pressed while the profile loads cannot add a newline
 * that ends the phrase and drops its results. Closed, it touches nothing, so Enter still
 * adds a newline in an ordinary draft. The handler is stable across renders.
 */
export function useDotPhraseMenu({
  enabled,
  actionPhrases = NO_ACTIONS,
  userId,
  isExpert,
  draftMessage,
  setDraftMessage,
  focusComposer,
}: UseDotPhraseMenuParams) {
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  const [dismissedDraft, setDismissedDraft] = useState<string | null>(null);

  /**
   * The draft, row and time of the last pick. A row fires on both press-in and
   * press on web, and a keyboard Enter can race a click; whichever comes
   * second, within the duplicate window, sees its own pick and does nothing.
   * The window is what lets a deliberate repeat through — backspacing to `.pu`
   * and pressing Tab again, or re-citing a paper after deleting it, restores an
   * earlier draft and must pick again.
   */
  const lastPickRef = useRef<{ key: string; at: number } | null>(null);

  const availablePhrases = useMemo<MenuPhrase[]>(
    () => [...(enabled && isExpert ? DOT_PHRASES : []), ...actionPhrases],
    [enabled, isExpert, actionPhrases]
  );

  const parsed = useMemo(
    () =>
      availablePhrases.length > 0
        ? parseTrailingDotPhrase<MenuPhrase>(draftMessage, availablePhrases)
        : null,
    [draftMessage, availablePhrases]
  );

  const commandTrigger =
    parsed?.kind === 'command' && !isAction(parsed.phrase) ? parsed.phrase.trigger : null;

  const publicationsQuery = useQuery({
    ...getAllUserPublicationsQueryOptions(Number(userId)),
    enabled: commandTrigger === 'mypubs',
  });

  const clinicalTrialsQuery = useQuery({
    ...getAllUserClinicalTrialsQueryOptions(Number(userId)),
    enabled: commandTrigger === 'mytrials',
  });

  const isOpen = parsed !== null && dismissedDraft !== draftMessage;

  const pick = useCallback(
    (rowKey: string, apply: () => void, { refocus = true }: { refocus?: boolean } = {}) => {
      const pickKey = `${draftMessage}\u0000${rowKey}`;

      const now = Date.now();

      const last = lastPickRef.current;

      if (last && last.key === pickKey && now - last.at < HALF_SECOND) {
        return;
      }

      lastPickRef.current = { key: pickKey, at: now };

      apply();

      if (refocus) {
        focusComposer?.();
      }
    },
    [draftMessage, focusComposer]
  );

  const toCitationRow = useCallback(
    (tokenStart: number, citation: Citation): DotPhraseMenuItem => ({
      key: citation.key,
      title: citation.title,
      subtitle: describeCitation(citation),
      accessibilityLabel: `Cite ${citation.title}`,
      onSelect: () =>
        pick(citation.key, () => {
          setDraftMessage(insertCitation(draftMessage, tokenStart, citation));
        }),
    }),
    [pick, setDraftMessage, draftMessage]
  );

  /**
   * A registry phrase completes its token and waits for a query. An action
   * runs at once: its token leaves the draft and the action opens whatever it
   * opens, which takes focus, so the composer is not refocused behind it.
   */
  const toPhraseRow = useCallback(
    (start: number, phrase: MenuPhrase): DotPhraseMenuItem => ({
      key: phrase.trigger,
      title: phrase.label,
      subtitle: phrase.description,
      accessibilityLabel: `${phrase.label}, ${phrase.description}`,
      onSelect: isAction(phrase)
        ? () =>
            pick(
              phrase.trigger,
              () => {
                setDraftMessage(removeActionToken(draftMessage, start));

                phrase.onRun(start);
              },
              { refocus: false }
            )
        : () =>
            pick(phrase.trigger, () =>
              setDraftMessage(completeDotPhrase(draftMessage, start, phrase))
            ),
    }),
    [pick, setDraftMessage, draftMessage]
  );

  const view = useMemo<MenuView>(() => {
    if (!parsed) {
      return { heading: '', items: [] };
    }

    if (parsed.kind === 'undotted') {
      const { phrase, query, start } = parsed;

      if (isAction(phrase)) {
        return { heading: '', items: [] };
      }

      const title = PROFILE_PHRASES[phrase.trigger]?.findTitle(query) ?? '';

      return {
        heading: 'Shortcuts',
        items: [
          {
            key: `undotted-${phrase.trigger}`,
            title,
            subtitle: `Shortcut: type ${phrase.label} before your words`,
            accessibilityLabel: title,
            onSelect: () =>
              pick(`undotted-${phrase.trigger}`, () => {
                setDraftMessage(addMissingDot(draftMessage, start, phrase, query));
              }),
          },
        ],
      };
    }

    if (parsed.kind === 'unmatched') {
      return {
        heading: 'Shortcuts',
        items: [],
        status: `No shortcut named “.${parsed.typed}”`,
        secondary: {
          heading: OTHER_SHORTCUTS_HEADING,
          items: availablePhrases.map((phrase) => toPhraseRow(parsed.start, phrase)),
        },
      };
    }

    if (parsed.kind === 'partial') {
      const { matches, start } = parsed;

      return {
        heading: 'Shortcuts',
        items: matches.map((phrase) => toPhraseRow(start, phrase)),
        secondary: {
          heading: OTHER_SHORTCUTS_HEADING,
          items: availablePhrases
            .filter((phrase) => !matches.includes(phrase))
            .map((phrase) => toPhraseRow(start, phrase)),
        },
      };
    }

    const { phrase, query, start } = parsed;

    if (isAction(phrase)) {
      return { heading: 'Shortcuts', items: [toPhraseRow(start, phrase)] };
    }

    const profileCopy = PROFILE_PHRASES[phrase.trigger];

    if (!profileCopy) {
      return { heading: '', items: [] };
    }

    const profileQuery = phrase.trigger === 'mytrials' ? clinicalTrialsQuery : publicationsQuery;

    if (profileQuery.isPending) {
      return {
        heading: profileCopy.heading,
        items: [],
        status: profileCopy.loading,
        isLoading: true,
      };
    }

    if (profileQuery.isError) {
      return { heading: profileCopy.heading, items: [], status: profileCopy.failed };
    }

    const citations =
      phrase.trigger === 'mytrials'
        ? filterClinicalTrials(clinicalTrialsQuery.data ?? [], query).map(clinicalTrialToCitation)
        : filterPublications(publicationsQuery.data ?? [], query).map(publicationToCitation);

    const matches = citations.slice(0, MAX_PROFILE_ROWS);

    let status: string | undefined;

    if ((profileQuery.data ?? []).length === 0) {
      status = profileCopy.noneOnProfile;
    } else if (matches.length === 0) {
      status = profileCopy.noMatches;
    }

    return {
      heading: profileCopy.heading,
      items: matches.map((citation) => toCitationRow(start, citation)),
      status,
    };
  }, [
    parsed,
    pick,
    setDraftMessage,
    draftMessage,
    availablePhrases,
    toPhraseRow,
    publicationsQuery,
    clinicalTrialsQuery,
    toCitationRow,
  ]);

  const { items } = view;

  const secondaryItems = view.secondary?.items ?? NO_ITEMS;

  const navigableItems = useMemo(() => [...items, ...secondaryItems], [items, secondaryItems]);

  const itemKeys = navigableItems.map((item) => item.key).join('|');

  useEffect(() => {
    setHighlightedIndex(0);
  }, [itemKeys]);

  const dismiss = useCallback(() => setDismissedDraft(draftMessage), [draftMessage]);

  /**
   * An undotted or unmatched suggestion is only an offer: the expert may simply
   * be writing a sentence that starts with the word, or a word such as `.NET`,
   * so it leaves Enter, Tab and the arrows to the composer. Escape still
   * dismisses it.
   */
  const ownsKeys = isOpen && parsed?.kind !== 'undotted' && parsed?.kind !== 'unmatched';

  /** What the stable key handler acts on, refreshed every render. */
  const keyStateRef = useRef({
    isOpen,
    ownsKeys,
    items: navigableItems,
    highlightedIndex,
    dismiss,
  });

  useLayoutEffect(() => {
    keyStateRef.current = { isOpen, ownsKeys, items: navigableItems, highlightedIndex, dismiss };
  });

  const handleKeyPress = useCallback((event: ComposerKeyEvent) => {
    const key = event?.nativeEvent?.key ?? event?.key;

    const state = keyStateRef.current;

    if (!state.isOpen || !key) {
      return;
    }

    if (key === 'Escape') {
      state.dismiss();

      event.preventDefault?.();

      return;
    }

    if (!state.ownsKeys) {
      return;
    }

    if (key === 'Enter' || key === 'Tab') {
      state.items[Math.min(state.highlightedIndex, state.items.length - 1)]?.onSelect();

      event.preventDefault?.();

      return;
    }

    if (state.items.length === 0) {
      return;
    }

    if (key === 'ArrowDown') {
      setHighlightedIndex((index) => Math.min(index + 1, state.items.length - 1));

      event.preventDefault?.();

      return;
    }

    if (key === 'ArrowUp') {
      setHighlightedIndex((index) => Math.max(index - 1, 0));

      event.preventDefault?.();
    }
  }, []);

  const menu: DotPhraseMenuProps | null = isOpen
    ? {
        heading: view.heading,
        items,
        highlightedIndex: ownsKeys ? highlightedIndex : -1,
        status: view.status,
        isLoading: view.isLoading,
        secondary: view.secondary,
        onDismiss: dismiss,
      }
    : null;

  return { menu, handleKeyPress };
}
